import { FootballGame, FootballLeague, GameStatus, FootballEvent, GameStatistic, GameTimelineEvent } from '@/types';
import { FOOTBALL_LEAGUES } from '@/constants/leagues';

const BASE_URL = 'https://v3.football.api-sports.io';

// Coloque sua chave em um arquivo .env: EXPO_PUBLIC_FOOTBALL_API_KEY=sua_chave
const API_KEY = process.env.EXPO_PUBLIC_FOOTBALL_API_KEY ?? '';

const headers = {
  'x-apisports-key': API_KEY,
};

const cache = new Map<string, { data: unknown; ts: number }>();
const CACHE_TTL = 30_000; // 30 segundos para dados live

function cached<T>(key: string, ttl: number, fetcher: () => Promise<T>): Promise<T> {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < ttl) return Promise.resolve(entry.data as T);
  return fetcher().then((data) => {
    cache.set(key, { data, ts: Date.now() });
    return data;
  });
}

function mapStatus(short: string, elapsed?: number): GameStatus {
  if (['1H', '2H', 'ET', 'P'].includes(short)) return 'live';
  if (short === 'HT') return 'halftime';
  if (['FT', 'AET', 'PEN'].includes(short)) return 'finished';
  if (short === 'NS') return 'scheduled';
  return 'scheduled';
}

function mapEvent(e: any, homeTeamId: string): FootballEvent {
  return {
    minute: e.time?.elapsed ?? 0,
    type: e.type === 'Goal' ? (e.detail === 'Own Goal' ? 'own_goal' : e.detail === 'Penalty' ? 'penalty' : 'goal')
         : e.type === 'Card' ? (e.detail === 'Red Card' ? 'red_card' : 'yellow_card')
         : 'substitution',
    team: String(e.team?.id ?? '') === homeTeamId ? 'home' : 'away',
    player: e.player?.name,
    assist: e.assist?.name,
  };
}

function footballEventToTimeline(event: FootballEvent, index: number): GameTimelineEvent {
  const type: GameTimelineEvent['type'] =
    event.type === 'goal' || event.type === 'own_goal' || event.type === 'penalty'
      ? 'goal'
      : event.type === 'yellow_card' || event.type === 'red_card'
        ? 'card'
        : event.type === 'substitution'
          ? 'substitution'
          : 'other';
  const title =
    event.type === 'goal' ? 'Gol'
    : event.type === 'own_goal' ? 'Gol contra'
    : event.type === 'penalty' ? 'Gol de pênalti'
    : event.type === 'yellow_card' ? 'Cartão amarelo'
    : event.type === 'red_card' ? 'Cartão vermelho'
    : 'Substituição';

  return {
    id: `${event.minute}-${event.type}-${event.player ?? 'event'}-${index}`,
    side: event.team,
    minute: `${event.minute}'`,
    type,
    title,
    detail: event.assist ? `${event.player ?? ''} · Assistência: ${event.assist}` : event.player,
  };
}

function statusTimeline(game: FootballGame): GameTimelineEvent[] {
  const items: GameTimelineEvent[] = [
    { id: 'kickoff', side: 'center', type: 'game_start', title: 'Início da partida' },
  ];
  if (game.status === 'halftime' || game.status === 'finished' || (game.minute ?? 0) > 45) {
    items.push({ id: 'halftime', side: 'center', type: 'halftime', title: 'Fim do primeiro tempo' });
  }
  if (game.status === 'live' && (game.minute ?? 0) > 45) {
    items.push({ id: 'second-half', side: 'center', type: 'period_start', title: 'Começou o segundo tempo' });
  }
  if (game.status === 'finished') {
    items.push({ id: 'fulltime', side: 'center', type: 'game_end', title: 'Fim de jogo' });
  }
  return items;
}

function mapGame(fixture: any): FootballGame {
  const f = fixture.fixture;
  const league = fixture.league;
  const teams = fixture.teams;
  const goals = fixture.goals;
  const homeTeamId = String(teams.home.id);
  const events: FootballEvent[] = (fixture.events ?? []).map((event: any) => mapEvent(event, homeTeamId));

  const game: FootballGame = {
    id: String(f.id),
    sport: 'futebol',
    league: {
      id: String(league.id),
      name: league.name,
      logo: league.logo,
      country: league.country,
      flag: league.flag,
    },
    homeTeam: {
      id: String(teams.home.id),
      name: teams.home.name,
      shortName: teams.home.name.slice(0, 3).toUpperCase(),
      logo: teams.home.logo,
    },
    awayTeam: {
      id: String(teams.away.id),
      name: teams.away.name,
      shortName: teams.away.name.slice(0, 3).toUpperCase(),
      logo: teams.away.logo,
    },
    score: {
      home: goals.home ?? 0,
      away: goals.away ?? 0,
    },
    status: mapStatus(f.status.short, f.status.elapsed),
    minute: f.status.elapsed,
    startTime: f.date,
    venue: f.venue?.name,
    events,
    timeline: [],
  };

  game.timeline = [...statusTimeline(game), ...events.map(footballEventToTimeline)];
  return game;
}

const FOOTBALL_STAT_LABELS: Record<string, string> = {
  'Ball Possession': 'Posse de bola',
  'Total Shots': 'Finalizações',
  'Shots on Goal': 'Finalizações no alvo',
  'Shots off Goal': 'Finalizações para fora',
  'Corner Kicks': 'Escanteios',
  Fouls: 'Faltas',
  Offsides: 'Impedimentos',
  'Yellow Cards': 'Cartões amarelos',
  'Red Cards': 'Cartões vermelhos',
  'Goalkeeper Saves': 'Defesas',
  'Total passes': 'Passes',
  'Passes accurate': 'Passes certos',
};

const FOOTBALL_STAT_ORDER = [
  'Ball Possession',
  'Total Shots',
  'Shots on Goal',
  'Shots off Goal',
  'Corner Kicks',
  'Fouls',
  'Offsides',
  'Yellow Cards',
  'Red Cards',
  'Goalkeeper Saves',
  'Total passes',
  'Passes accurate',
];

function statNumber(value: unknown): number | undefined {
  const parsed = Number.parseFloat(String(value ?? '').replace('%', '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
}

async function getFootballStatistics(fixtureId: string): Promise<GameStatistic[] | undefined> {
  if (!API_KEY) return undefined;
  try {
    const res = await fetch(`${BASE_URL}/fixtures/statistics?fixture=${fixtureId}`, { headers });
    if (!res.ok) return undefined;
    const json = await res.json();
    const teams: any[] = json.response ?? [];
    const home = teams[0];
    const away = teams[1];
    if (!home || !away) return undefined;
    const homeStats = new Map((home.statistics ?? []).map((s: any) => [s.type, s.value]));
    const awayStats = new Map((away.statistics ?? []).map((s: any) => [s.type, s.value]));
    const rows: GameStatistic[] = [];
    FOOTBALL_STAT_ORDER.forEach((name) => {
      const homeValue = homeStats.get(name);
      const awayValue = awayStats.get(name);
      if (homeValue == null && awayValue == null) return;
      rows.push({
        id: name,
        label: FOOTBALL_STAT_LABELS[name] ?? name,
        home: String(homeValue ?? '-'),
        away: String(awayValue ?? '-'),
        homeValue: statNumber(homeValue),
        awayValue: statNumber(awayValue),
      });
    });
    return rows.length > 0 ? rows : undefined;
  } catch {
    return undefined;
  }
}

export async function getLiveFootballGames(): Promise<FootballGame[]> {
  return cached('football:live', CACHE_TTL, async () => {
    const res = await fetch(`${BASE_URL}/fixtures?live=all`, { headers });
    const json = await res.json();
    if (!json.response) return [];
    return (json.response as any[]).map(mapGame);
  });
}

export async function getFootballGamesByLeague(leagueId: string, season: number): Promise<FootballGame[]> {
  const today = new Date().toISOString().slice(0, 10);
  return cached(`football:league:${leagueId}:${today}`, 60_000, async () => {
    const res = await fetch(
      `${BASE_URL}/fixtures?league=${leagueId}&season=${season}&date=${today}`,
      { headers }
    );
    const json = await res.json();
    if (!json.response) return [];
    return (json.response as any[]).map(mapGame);
  });
}

export async function getFootballTodayGames(): Promise<FootballGame[]> {
  const leagueIds = FOOTBALL_LEAGUES.map((l) => l.id);
  const today = new Date().toISOString().slice(0, 10);

  return cached(`football:today:${today}`, 120_000, async () => {
    const res = await fetch(
      `${BASE_URL}/fixtures?date=${today}&timezone=America/Sao_Paulo`,
      { headers }
    );
    const json = await res.json();

    if (json.errors && Object.keys(json.errors).length > 0) {
      const msg = Object.values(json.errors).join(', ');
      // Plano free não acessa 2026 — retorna vazio para cair no fallback ESPN/SofaScore (sem erro vermelho)
      console.log('[Football API] api-sports indisponível, usando fallback:', msg);
      return [];
    }

    if (!json.response) {
      console.log('[Football API] Sem campo response, usando fallback');
      return [];
    }

    const total = (json.response as any[]).length;
    const games = (json.response as any[]).map(mapGame);
    const filtered = games.filter((g) => leagueIds.includes(g.league.id));
    console.log(`[Football API] Total fixtures hoje: ${total}, nas ligas configuradas: ${filtered.length}`);
    return filtered;
  });
}

export async function getFootballGameDetail(fixtureId: string): Promise<FootballGame | null> {
  return cached(`football:fixture:${fixtureId}`, CACHE_TTL, async () => {
    const res = await fetch(`${BASE_URL}/fixtures?id=${fixtureId}`, { headers });
    const json = await res.json();
    if (!json.response?.length) return null;
    const game = mapGame(json.response[0]);
    game.statistics = await getFootballStatistics(fixtureId);
    return game;
  });
}

// Todos os jogos da Copa do Mundo FIFA 2026 (grupos + fases eliminatórias)
export async function getWorldCupGames(): Promise<FootballGame[]> {
  return cached('football:worldcup:2026', 300_000, async () => {
    const res = await fetch(
      `${BASE_URL}/fixtures?league=1&season=2026&timezone=America/Sao_Paulo`,
      { headers }
    );
    const json = await res.json();

    if (json.errors && Object.keys(json.errors).length > 0) {
      const msg = Object.values(json.errors).join(', ');
      // Plano free não acessa a temporada 2026 — cai no fallback ESPN silenciosamente
      console.log('[Football API] Copa via api-sports indisponível, usando ESPN:', msg);
      return [];
    }

    if (!json.response) return [];
    const games = (json.response as any[]).map(mapGame);
    console.log(`[Football API] Copa do Mundo 2026: ${games.length} jogos`);
    return games;
  });
}

// Jogos da Copa do Mundo de hoje
export async function getWorldCupTodayGames(): Promise<FootballGame[]> {
  const all = await getWorldCupGames();
  const today = new Date().toISOString().slice(0, 10);
  return all.filter((g) => g.startTime.slice(0, 10) === today);
}

// Jogos ao vivo da Copa do Mundo
export async function getWorldCupLiveGames(): Promise<FootballGame[]> {
  const all = await getWorldCupGames();
  return all.filter((g) => g.status === 'live' || g.status === 'halftime');
}
